import { EventEmitter, Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
    ConfirmDialogAction,
    DeleteDialogAction,
    InfoDialogAction,
    PromptDialogAction,
    PromptDialogActionConfig,
} from '../../models/simple-dialog.model';
import { ConfirmInfoPopupComponent } from '../components/modals/confirm-info-popup/confirm-info-popup.component';
import { ErrorPopupComponent } from '../components/modals/error-popup/error-popup.component';
import { SnackbarComponent, SnackbarSettings } from '../components/modals/snackbar/snackbar.component';
import { DomComponent, DomService } from './dom.service';

@Injectable({
    providedIn: 'root',
})
export class PopupService {
    private readonly domService = inject(DomService);
    private readonly translate = inject(TranslateService);

    private snackbar?: DomComponent<SnackbarComponent>;

    public showSnackbar(message: string, settings?: Omit<SnackbarSettings, 'text'>): void {
        const translated = this.translate.instant(message);

        if (this.snackbar && !this.snackbar.isRemoved) {
            this.snackbar.component.text = translated;

            if (settings?.actions) {
                this.snackbar.component.actions = settings.actions;
            }
            this.snackbar.component.restart(settings?.duration);

            return;
        }

        const snackbar = this.domService.appendComponentTo<SnackbarComponent, SnackbarSettings>(
            'overlay-container',
            SnackbarComponent,
            {
                text: translated,
                actions: settings?.actions ?? [
                    {
                        label: this.translate.instant('comp.snackbar.dismiss'),
                        onClick: (): void => {
                            snackbar.remove();
                        },
                    },
                ],
                duration: settings?.duration ?? 5000,
            }
        );

        this.snackbar = snackbar;
        snackbar.on('remove', () => {
            if (this.snackbar === snackbar) this.snackbar = undefined;
        });
    }

    public showInfo(message: string, title?: string): EventEmitter<boolean | null> {
        return this.showDialog({ action: 'info', message, title });
    }

    public showConfirm(
        message: string,
        title?: string,
        confirmText?: string,
        cancelText?: string
    ): EventEmitter<boolean | null> {
        return this.showDialog({ action: 'confirm', message, title, confirmText, cancelText });
    }

    public showDelete(message: string, title?: string): EventEmitter<boolean | null> {
        return this.showDialog({ action: 'delete', message, title });
    }

    public showPrompt(
        message: string,
        title?: string,
        config?: Partial<PromptDialogActionConfig>
    ): EventEmitter<string | null> {
        return this.showDialog<string>({ action: 'prompt', message, title, config: config });
    }

    public showError(title: string, message: string, errorCode: string): void {
        title = this.translate.instant(title);
        message = this.translate.instant(message);
        this.domService.appendComponentTo<ErrorPopupComponent>('overlay-container', ErrorPopupComponent, {
            error: {
                type: 'DIALOG',
                title,
                message,
                errorCode,
            },
        });
    }

    private showDialog<TResult extends string | boolean>(
        options: InfoDialogAction | ConfirmDialogAction | PromptDialogAction | DeleteDialogAction
    ): EventEmitter<TResult> {
        return this.domService.appendComponentTo<ConfirmInfoPopupComponent<TResult>>(
            'overlay-container',
            ConfirmInfoPopupComponent,
            { options }
        ).component.onClose;
    }
}
